/**
 * Phase 16 Plan 16-03 — WorkOrderDetailPage (KPI tab upgraded in Phase 17 Plan 17-02)
 *
 * Route: /work-orders/:id
 * Permission gate: workorders.view
 *
 * 5 tabs: Overview / Machines / KPIs (live — Phase 17) / Stats / Activity
 * Lifecycle action buttons (perm-gated):
 *   - Activate (Draft + has machines)
 *   - Complete (Active)
 *   - Reopen  (Completed)
 *   - Cancel  (not Cancelled)
 * Edit button → /work-orders/:id/edit (perm-gated: workorders.edit)
 */

import { ArrowLeftIcon, StarIcon } from '@phosphor-icons/react';
import { useMemo, useState, type JSX } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { CreateKpiDialog } from '@/components/kpis/CreateKpiDialog';
import { DeactivateKpiConfirmDialog } from '@/components/kpis/DeactivateKpiConfirmDialog';
import { EditKpiDialog } from '@/components/kpis/EditKpiDialog';
import { KpiTemplatePickerDialog } from '@/components/kpis/KpiTemplatePickerDialog';
import { KpiUnitDisplay } from '@/components/kpis/KpiUnitDisplay';
import { ReactivateKpiDialog } from '@/components/kpis/ReactivateKpiDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ActivateWorkOrderDialog } from '@/components/work-orders/ActivateWorkOrderDialog';
import { CancelWorkOrderDialog } from '@/components/work-orders/CancelWorkOrderDialog';
import { CompleteWorkOrderDialog } from '@/components/work-orders/CompleteWorkOrderDialog';
import { LinkMachinesDialog } from '@/components/work-orders/LinkMachinesDialog';
import { ReopenWorkOrderDialog } from '@/components/work-orders/ReopenWorkOrderDialog';
import { UnlinkMachinesConfirmDialog } from '@/components/work-orders/UnlinkMachinesConfirmDialog';
import { WorkOrderSitesTab } from '@/components/work-orders/WorkOrderSitesTab';
import { WorkOrderStatusBadge } from '@/components/work-orders/WorkOrderStatusBadge';
import { useKpisQuery, useTogglePinKpiMutation } from '@/hooks/useKpiQueries';
import { useWorkOrderAuditQuery, useWorkOrderQuery } from '@/hooks/useWorkOrderQueries';
import { useAuth } from '@/lib/auth/AuthContext';
import { toast } from '@/lib/toast';
import { formatMachineName } from '@/lib/utils/machine-label';
import type { CreateKpiBody, KpiListItem } from '@/types/kpi';
import type { KpiTemplate } from '@/types/kpi-template';
import { isActiveSiteLink, type WorkOrderMachineItem } from '@/types/work-orders';

function formatDate(d: string | null): string {
  if (d === null || d === '') return '—';
  try {
    return new Date(d).toLocaleDateString();
  } catch {
    return d;
  }
}

function formatDateTime(d: string | null): string {
  if (d === null || d === '') return '—';
  try {
    return new Date(d).toLocaleString();
  } catch {
    return d;
  }
}

// ---------------------------------------------------------------------------
// KPI Tab — extracted for clarity
// ---------------------------------------------------------------------------

interface KpiTabProps {
  readonly woId: number;
  readonly workOrderMachines: readonly WorkOrderMachineItem[];
  readonly canCreate: boolean;
  readonly canEdit: boolean;
}

const MAX_PINS_PER_WO = 3;

function KpiTab({ woId, workOrderMachines, canCreate, canEdit }: KpiTabProps): JSX.Element {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<KpiListItem | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<KpiListItem | null>(null);
  const [reactivateTarget, setReactivateTarget] = useState<KpiListItem | null>(null);
  const [templateInitialValues, setTemplateInitialValues] = useState<
    Partial<CreateKpiBody> | undefined
  >(undefined);

  const kpisQuery = useKpisQuery(woId, includeInactive);
  const kpisData = kpisQuery.data;
  const kpis = useMemo(() => kpisData ?? [], [kpisData]);

  // Phase 23 (Plan 23-03) D-239 — pin/unpin canonical optimistic mutation.
  // The hook applies the optimistic flip to the cached `['kpis', woId]` list
  // immediately so the StarIcon weight switches without a network round-trip.
  // On 400 (max-3 cap exceeded server-side) the rollback restores the prior
  // cache snapshot AND we surface a toast to explain the failure.
  const togglePin = useTogglePinKpiMutation(woId);

  // Defense-in-depth: count active pinned KPIs locally so the star button
  // disables itself when the user is at the cap. Server remains authoritative
  // (RESEARCH §4); this just stops the obvious 4th-pin click.
  const pinnedCount = useMemo(
    () => kpis.filter((k) => k.isPinnedToDashboard === true && k.isActive).length,
    [kpis],
  );

  const handleTogglePin = (kpi: KpiListItem): void => {
    const nextPinned = !(kpi.isPinnedToDashboard === true);
    togglePin.mutate(
      { kpiId: kpi.id, pinned: nextPinned },
      {
        onError: (err) => {
          // Surface server-side max-3 / permission denials. Optimistic flip
          // already rolled back inside the hook's onError handler.
          toast.error('Could not update pin', {
            description:
              err.message === 'tooManyPins' || err.status === 400
                ? 'A work order may not have more than 3 pinned KPIs.'
                : err.message,
          });
        },
      },
    );
  };

  function handleTemplateSelected(template: KpiTemplate): void {
    setTemplateInitialValues({
      name: template.name,
      unit: template.unit,
      ...(template.customUnitText !== null ? { customUnitText: template.customUnitText } : {}),
      ...(template.description !== null ? { description: template.description } : {}),
    });
    setCreateOpen(true);
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle>KPIs</CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            {/* Include inactive toggle */}
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="kpi-include-inactive-label">
              <input
                type="checkbox"
                data-testid="kpi-include-inactive-toggle"
                checked={includeInactive}
                onChange={(e) => { setIncludeInactive(e.target.checked); }}
                className="cursor-pointer"
              />
              Include inactive
            </label>

            {canCreate && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => { setPickerOpen(true); }}
                  data-testid="kpi-use-template-btn"
                >
                  Use Template
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setTemplateInitialValues(undefined);
                    setCreateOpen(true);
                  }}
                  data-testid="kpi-add-btn"
                >
                  Add KPI
                </Button>
              </>
            )}
          </div>
        </div>
        {kpisQuery.isLoading && (
          <CardDescription data-testid="kpi-list-loading">Loading KPIs…</CardDescription>
        )}
      </CardHeader>

      <CardContent>
        {kpisQuery.isError && (
          <p className="text-sm text-destructive" data-testid="kpi-list-error">
            Failed to load KPIs.
          </p>
        )}

        <div className="overflow-x-auto">
        <Table data-testid="kpi-list-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Target/Shift</TableHead>
              <TableHead>Machines</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {kpis.length === 0 && !kpisQuery.isLoading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6">
                  <span className="text-muted-foreground" data-testid="kpi-list-empty">
                    No KPIs found.
                  </span>
                </TableCell>
              </TableRow>
            ) : (
              kpis.map((kpi) => (
                <TableRow key={kpi.id} data-testid={`kpi-row-${String(kpi.id)}`}>
                  <TableCell className="font-medium">{kpi.name}</TableCell>
                  <TableCell>
                    <KpiUnitDisplay unit={kpi.unit} customUnitText={kpi.customUnitText} />
                  </TableCell>
                  <TableCell>
                    {kpi.targetPerShift !== null ? String(kpi.targetPerShift) : '—'}
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium"
                      data-testid={`kpi-machine-count-${String(kpi.id)}`}
                    >
                      {kpi.applicableMachineCount}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span
                      className={kpi.isActive ? 'text-green-600' : 'text-muted-foreground'}
                      data-testid={`kpi-status-${String(kpi.id)}`}
                    >
                      {kpi.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      {/* Phase 23 Plan 23-03 D-239 — pin/unpin star.
                       *  Visible whenever the caller holds kpis.edit (server
                       *  re-checks). Disabled at the 3-pin cap when this row
                       *  is currently unpinned (defense-in-depth UX guard;
                       *  server remains authoritative).
                       */}
                      {canEdit && kpi.isActive && (() => {
                        const isPinned = kpi.isPinnedToDashboard === true;
                        const atCap = !isPinned && pinnedCount >= MAX_PINS_PER_WO;
                        return (
                          <Button
                            type="button"
                            size="icon"
                            variant="ghost"
                            disabled={atCap}
                            aria-label={
                              isPinned ? `Unpin ${kpi.name}` : `Pin ${kpi.name}`
                            }
                            title={
                              atCap
                                ? 'Max 3 pinned KPIs per work order'
                                : isPinned
                                  ? 'Unpin from dashboard'
                                  : 'Pin to dashboard'
                            }
                            onClick={() => { handleTogglePin(kpi); }}
                            data-testid={`kpi-pin-btn-${String(kpi.id)}`}
                          >
                            <StarIcon
                              size={16}
                              weight={isPinned ? 'fill' : 'regular'}
                              data-testid={`kpi-pin-icon-${String(kpi.id)}`}
                            />
                          </Button>
                        );
                      })()}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => { setEditTarget(kpi); }}
                        data-testid={`kpi-edit-btn-${String(kpi.id)}`}
                      >
                        Edit
                      </Button>
                      {kpi.isActive ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="destructive"
                          onClick={() => { setDeactivateTarget(kpi); }}
                          data-testid={`kpi-deactivate-btn-${String(kpi.id)}`}
                        >
                          Deactivate
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => { setReactivateTarget(kpi); }}
                          data-testid={`kpi-reactivate-btn-${String(kpi.id)}`}
                        >
                          Reactivate
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </div>
      </CardContent>

      {/* Dialogs */}
      {createOpen && (
        <CreateKpiDialog
          open={createOpen}
          onOpenChange={(o) => {
            if (!o) setTemplateInitialValues(undefined);
            setCreateOpen(o);
          }}
          workOrderId={woId}
          workOrderMachines={workOrderMachines}
          initialValues={templateInitialValues}
        />
      )}

      {pickerOpen && (
        <KpiTemplatePickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={handleTemplateSelected}
        />
      )}

      {editTarget !== null && (
        <EditKpiDialog
          open={true}
          onOpenChange={(o) => { if (!o) setEditTarget(null); }}
          workOrderId={woId}
          kpiId={editTarget.id}
          workOrderMachines={workOrderMachines}
        />
      )}

      {deactivateTarget !== null && (
        <DeactivateKpiConfirmDialog
          open={true}
          onOpenChange={(o) => { if (!o) setDeactivateTarget(null); }}
          workOrderId={woId}
          kpiId={deactivateTarget.id}
          kpiName={deactivateTarget.name}
        />
      )}

      {reactivateTarget !== null && (
        <ReactivateKpiDialog
          open={true}
          onOpenChange={(o) => { if (!o) setReactivateTarget(null); }}
          workOrderId={woId}
          kpiId={reactivateTarget.id}
          kpiName={reactivateTarget.name}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function WorkOrderDetailPage(): JSX.Element {
  const { id: idParam } = useParams<{ id: string }>();
  const id = Number(idParam);
  const { permissions } = useAuth();

  const [activateOpen, setActivateOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [unlinkTarget, setUnlinkTarget] = useState<WorkOrderMachineItem | null>(null);

  const [auditPage, setAuditPage] = useState(1);

  const detailQuery = useWorkOrderQuery(id);
  const auditQuery = useWorkOrderAuditQuery(id, auditPage);

  if (!permissions.includes('workorders.view')) {
    return <Navigate to="/dashboard" replace />;
  }

  if (detailQuery.isLoading) {
    return (
      <p className="text-muted-foreground text-sm" data-testid="wo-detail-loading">
        Loading work order…
      </p>
    );
  }

  if (detailQuery.isError) {
    const status = detailQuery.error.status;
    if (status === 404) {
      return (
        <div className="text-center py-16" data-testid="wo-detail-not-found">
          <p className="text-lg font-medium">Work Order not found.</p>
          <Button asChild variant="link" className="mt-2">
            <Link to="/work-orders">Back to list</Link>
          </Button>
        </div>
      );
    }
    return (
      <p className="text-destructive text-sm" data-testid="wo-detail-error">
        Failed to load work order.
      </p>
    );
  }

  const wo = detailQuery.data;
  if (wo === undefined) return <></>;

  const canEdit = permissions.includes('workorders.edit');
  const canActivate = permissions.includes('workorders.activate');
  const canComplete = permissions.includes('workorders.complete');
  const canCancel = permissions.includes('workorders.cancel');
  const canCreateKpi = permissions.includes('kpis.create');
  // Phase 23 Plan 23-03 D-239 — `kpis.edit` gates pin/unpin per CONTEXT.md.
  const canEditKpi = permissions.includes('kpis.edit');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Button asChild variant="ghost" size="sm" className="mb-2">
          <Link to="/work-orders">
            <ArrowLeftIcon size={16} /> Work Orders
          </Link>
        </Button>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold tracking-tight font-mono">{wo.woNumber}</h1>
              <WorkOrderStatusBadge status={wo.status} />
            </div>
            {wo.clientId !== undefined ? (
              <p className="text-muted-foreground">
                Client:{' '}
                <Link
                  to={`/clients/${String(wo.clientId)}`}
                  className="text-primary hover:underline"
                  data-testid="wo-detail-client-link"
                >
                  {wo.clientName ?? String(wo.clientId)}
                </Link>
              </p>
            ) : null}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {canActivate && wo.status === 'Draft' && wo.machines.length > 0 && (
              <Button
                type="button"
                size="sm"
                onClick={() => { setActivateOpen(true); }}
                data-testid="wo-detail-activate-btn"
              >
                Activate
              </Button>
            )}
            {canComplete && wo.status === 'Active' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setCompleteOpen(true); }}
                data-testid="wo-detail-complete-btn"
              >
                Complete
              </Button>
            )}
            {canActivate && wo.status === 'Completed' && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => { setReopenOpen(true); }}
                data-testid="wo-detail-reopen-btn"
              >
                Reopen
              </Button>
            )}
            {canCancel && wo.status !== 'Cancelled' && (
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={() => { setCancelOpen(true); }}
                data-testid="wo-detail-cancel-btn"
              >
                Cancel
              </Button>
            )}
            {canEdit && (
              <Button asChild size="sm" variant="outline">
                <Link to={`/work-orders/${String(wo.id)}/edit`} data-testid="wo-detail-edit-btn">
                  Edit
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview" data-testid="wo-detail-tabs">
        <div className="overflow-x-auto">
        <TabsList>
          <TabsTrigger value="overview" data-testid="wo-tab-overview">
            Overview
          </TabsTrigger>
          <TabsTrigger value="sites" data-testid="wo-tab-sites">
            Sites ({(wo.sites ?? []).filter(isActiveSiteLink).length})
          </TabsTrigger>
          <TabsTrigger value="machines" data-testid="wo-tab-machines">
            Machines ({wo.machines.length})
          </TabsTrigger>
          <TabsTrigger value="kpis" data-testid="wo-tab-kpis">
            KPIs ({wo.kpisCount})
          </TabsTrigger>
          <TabsTrigger value="stats" data-testid="wo-tab-stats">
            Stats
          </TabsTrigger>
          <TabsTrigger value="activity" data-testid="wo-tab-activity">
            Activity
          </TabsTrigger>
        </TabsList>
        </div>

        {/* Overview */}
        <TabsContent value="overview" data-testid="wo-tab-content-overview">
          <Card>
            <CardHeader>
              <CardTitle>Overview</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="grid grid-cols-1 gap-x-8 gap-y-3 sm:grid-cols-2">
                <div>
                  <p className="text-muted-foreground font-medium">Site</p>
                  {/* eslint-disable-next-line @typescript-eslint/no-deprecated -- legacy single-site display per Plan 22-03 D-228 */}
                  <p>{wo.siteName}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Client</p>
                  {wo.clientId !== undefined ? (
                    <Link
                      to={`/clients/${String(wo.clientId)}`}
                      className="text-primary hover:underline text-sm"
                    >
                      {wo.clientName ?? String(wo.clientId)}
                    </Link>
                  ) : (
                    <p className="text-muted-foreground">—</p>
                  )}
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">Start Date</p>
                  <p>{formatDate(wo.startDate)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground font-medium">End Date</p>
                  <p>{formatDate(wo.endDate)}</p>
                </div>
                {wo.completionDate !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Completion Date</p>
                    <p>{formatDate(wo.completionDate)}</p>
                  </div>
                )}
                {wo.cancellationReason !== null && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground font-medium">Cancellation Reason</p>
                    <p>{wo.cancellationReason}</p>
                  </div>
                )}
                {wo.description !== null && (
                  <div className="col-span-2">
                    <p className="text-muted-foreground font-medium">Description</p>
                    <p>{wo.description}</p>
                  </div>
                )}
                {wo.activatedAt !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Activated</p>
                    <p>{formatDateTime(wo.activatedAt)}</p>
                  </div>
                )}
                {wo.completedAt !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Completed</p>
                    <p>{formatDateTime(wo.completedAt)}</p>
                  </div>
                )}
                {wo.cancelledAt !== null && (
                  <div>
                    <p className="text-muted-foreground font-medium">Cancelled</p>
                    <p>{formatDateTime(wo.cancelledAt)}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sites */}
        <TabsContent value="sites" data-testid="wo-tab-content-sites">
          <WorkOrderSitesTab workOrder={wo} canEdit={canEdit} />
        </TabsContent>

        {/* Machines */}
        <TabsContent value="machines" data-testid="wo-tab-content-machines">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Linked Machines</CardTitle>
                {canEdit && (
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => { setLinkOpen(true); }}
                    data-testid="wo-detail-add-machines-btn"
                  >
                    Add Machines
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
              <Table data-testid="wo-machines-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Linked At</TableHead>
                    {canEdit && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {wo.machines.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={canEdit ? 5 : 4} className="text-center py-6">
                        <span className="text-muted-foreground" data-testid="wo-machines-empty">
                          No machines linked.
                        </span>
                      </TableCell>
                    </TableRow>
                  ) : (
                    wo.machines.map((m) => (
                      <TableRow key={m.id} data-testid={`wo-machine-row-${String(m.machineId)}`}>
                        <TableCell className="font-mono text-xs">{m.machineCode}</TableCell>
                        <TableCell>{formatMachineName(m.machineName, m.vehicleNumber)}</TableCell>
                        <TableCell>{m.machineTypeName}</TableCell>
                        <TableCell>{formatDateTime(m.linkedAt)}</TableCell>
                        {canEdit && (
                          <TableCell>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => { setUnlinkTarget(m); }}
                              data-testid={`wo-unlink-machine-${String(m.machineId)}`}
                            >
                              Unlink
                            </Button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* KPIs — live (Phase 17-02; pin button added Phase 23-03 D-239) */}
        <TabsContent value="kpis" data-testid="wo-tab-content-kpis">
          <KpiTab
            woId={wo.id}
            workOrderMachines={wo.machines}
            canCreate={canCreateKpi}
            canEdit={canEditKpi}
          />
        </TabsContent>

        {/* Stats */}
        <TabsContent value="stats" data-testid="wo-tab-content-stats">
          <Card>
            <CardHeader>
              <CardTitle>Stats</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="rounded-md border p-3">
                  <p className="text-muted-foreground">Machines</p>
                  <p className="text-2xl font-bold">{wo.machines.length}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-muted-foreground">KPIs</p>
                  <p className="text-2xl font-bold">{wo.kpisCount}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-muted-foreground">Total Entries</p>
                  <p className="text-2xl font-bold text-muted-foreground">—</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-muted-foreground">By Status</p>
                  <p className="text-sm text-muted-foreground">Phase 17</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Activity */}
        <TabsContent value="activity" data-testid="wo-tab-content-activity">
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              {auditQuery.isLoading && (
                <p className="text-sm text-muted-foreground">Loading activity…</p>
              )}
              {auditQuery.data && (
                <>
                  <div className="overflow-x-auto">
                  <Table data-testid="wo-audit-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>Action</TableHead>
                        <TableHead>At</TableHead>
                        <TableHead>By</TableHead>
                        <TableHead>Details</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditQuery.data.items.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center py-6">
                            <span
                              className="text-muted-foreground"
                              data-testid="wo-audit-empty"
                            >
                              No activity recorded.
                            </span>
                          </TableCell>
                        </TableRow>
                      ) : (
                        auditQuery.data.items.map((log) => (
                          <TableRow
                            key={log.id}
                            data-testid={`wo-audit-row-${String(log.id)}`}
                          >
                            <TableCell>{log.action}</TableCell>
                            <TableCell>{formatDateTime(log.at)}</TableCell>
                            <TableCell>{log.byUserName ?? String(log.byUserId)}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {log.details ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  </div>
                  <div className="flex items-center justify-between pt-2">
                    <p className="text-xs text-muted-foreground">
                      Page {auditPage} • {auditQuery.data.total} total
                    </p>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { setAuditPage((p) => Math.max(1, p - 1)); }}
                        disabled={auditPage <= 1}
                        data-testid="wo-audit-prev"
                      >
                        Previous
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => { setAuditPage((p) => p + 1); }}
                        disabled={
                          auditPage * 20 >= auditQuery.data.total
                        }
                        data-testid="wo-audit-next"
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      {activateOpen && (
        <ActivateWorkOrderDialog
          open={activateOpen}
          onOpenChange={setActivateOpen}
          workOrder={wo}
        />
      )}
      {completeOpen && (
        <CompleteWorkOrderDialog
          open={completeOpen}
          onOpenChange={setCompleteOpen}
          workOrder={wo}
        />
      )}
      {reopenOpen && (
        <ReopenWorkOrderDialog
          open={reopenOpen}
          onOpenChange={setReopenOpen}
          workOrder={wo}
        />
      )}
      {cancelOpen && (
        <CancelWorkOrderDialog
          open={cancelOpen}
          onOpenChange={setCancelOpen}
          workOrder={wo}
        />
      )}
      {linkOpen && (
        <LinkMachinesDialog
          open={linkOpen}
          onOpenChange={setLinkOpen}
          workOrder={wo}
        />
      )}
      {unlinkTarget !== null && (
        <UnlinkMachinesConfirmDialog
          open={true}
          onOpenChange={(o) => { if (!o) setUnlinkTarget(null); }}
          workOrder={wo}
          machineIds={[unlinkTarget.machineId]}
          machineLabels={[unlinkTarget.machineCode]}
        />
      )}
    </div>
  );
}
